import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Trash, Key, ShieldStar, CircleNotch, BellRinging, PaperPlaneTilt, PencilSimple, Archive, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export default function Admin() {
  const { isPrimary } = useAuth();
  const [section, setSection] = useState("tournaments");
  const sections = [
    ["tournaments", "Tournaments"],
    ["users", "Users"],
    ["notifications", "Notifications"],
  ];
  return (
    <div className="animate-fade-up">
      <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter mb-1">Admin</h1>
      <p className="text-muted-foreground mb-6">Manage tournaments, players, payments and reminders.</p>
      <div className="flex gap-1 border-b border-border mb-6">
        {sections.map(([k, l]) => (
          <button key={k} onClick={() => setSection(k)} data-testid={`admin-tab-${k}`}
            className={`px-4 py-3 font-heading font-bold uppercase tracking-wide text-sm border-b-2 -mb-px transition-colors ${section === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>
      {section === "tournaments" && <TournamentsAdmin />}
      {section === "users" && <UsersAdmin isPrimary={isPrimary} />}
      {section === "notifications" && <NotificationsAdmin />}
    </div>
  );
}

/* ---------------- Tournaments admin ---------------- */
function TournamentsAdmin() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => setList((await api.get(`/tournaments?scope=${showArchived ? "archived" : "active"}`)).data);
  useEffect(() => { load(); }, [showArchived]);

  const del = async (id) => {
    if (!window.confirm("Delete this tournament and all its entries/results? This cannot be undone.")) return;
    await api.delete(`/tournaments/${id}`);
    toast.success("Deleted");
    load();
  };

  const setArchived = async (id, archived) => {
    await api.patch(`/tournaments/${id}`, { archived });
    toast.success(archived ? "Tournament archived" : "Tournament restored");
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Button onClick={() => setOpen(true)} data-testid="new-tournament-button" className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest gap-1.5">
          <Plus size={18} weight="bold" /> New Tournament
        </Button>
        <button
          onClick={() => setShowArchived((v) => !v)}
          data-testid="toggle-archived"
          className={`flex items-center gap-1.5 border rounded-sm px-3 py-2 font-bold uppercase text-xs tracking-widest transition-colors ${showArchived ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-white"}`}
        >
          <Archive size={16} weight="bold" /> {showArchived ? "Viewing archived" : "Show archived"}
        </button>
      </div>
      {list === null ? <CircleNotch size={28} className="animate-spin text-primary" /> : (
        <div className="space-y-2">
          {list.map((t) => (
            <div key={t.id} className="border border-border bg-card rounded-sm p-4 flex items-center justify-between gap-3" data-testid={`admin-tournament-${t.id}`}>
              <div>
                <div className="font-heading font-bold text-xl uppercase tracking-tight flex items-center gap-2">
                  {t.name}
                  {t.archived && <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground border border-border rounded-sm px-1.5 py-0.5">Archived</span>}
                </div>
                <div className="text-sm text-muted-foreground">{t.status.replace("_", " ")} · {t.entry_count} players · {t.games?.length} games</div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => navigate(`/tournament/${t.id}`)} className="bg-secondary hover:bg-[#1E1E1E] border border-border text-white font-bold uppercase text-xs" data-testid={`manage-${t.id}`}>Manage</Button>
                {t.archived ? (
                  <button onClick={() => setArchived(t.id, false)} className="p-2 text-muted-foreground hover:text-primary" title="Restore from archive" data-testid={`unarchive-tournament-${t.id}`}><ArrowCounterClockwise size={18} weight="bold" /></button>
                ) : (
                  <button onClick={() => setArchived(t.id, true)} className="p-2 text-muted-foreground hover:text-white" title="Archive" data-testid={`archive-tournament-${t.id}`}><Archive size={18} weight="bold" /></button>
                )}
                <button onClick={() => del(t.id)} className="p-2 text-muted-foreground hover:text-kinetic" title="Delete" data-testid={`delete-tournament-${t.id}`}><Trash size={18} weight="bold" /></button>
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-muted-foreground">{showArchived ? "No archived tournaments." : "No tournaments yet."}</p>}
        </div>
      )}
      {open && <CreateTournament onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function CreateTournament({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [entryFee, setEntryFee] = useState("");
  const [numGames, setNumGames] = useState(6);
  const [maxEntries, setMaxEntries] = useState("");
  const [paymentDeadline, setPaymentDeadline] = useState("");
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [firstDate, setFirstDate] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      let games = null;
      if (firstDate) {
        const start = new Date(firstDate);
        games = Array.from({ length: Number(numGames) }, (_, i) => {
          const d = new Date(start.getTime() + i * 14 * 24 * 3600 * 1000); // fortnightly
          return { name: `Game ${i + 1}`, scheduled_at: d.toISOString() };
        });
      }
      await api.post("/tournaments", {
        name: name.trim(), description, entry_fee: Number(entryFee) || 0,
        num_games: Number(numGames), games,
        max_entries: Number(maxEntries) || 0,
        payment_deadline: paymentDeadline ? new Date(paymentDeadline).toISOString() : null,
        requires_approval: requiresApproval,
      });
      toast.success("Tournament created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">New Tournament</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <L label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} className="bg-secondary border-border" data-testid="ct-name" /></L>
          <L label="Description"><Input value={description} onChange={(e) => setDescription(e.target.value)} className="bg-secondary border-border" data-testid="ct-desc" /></L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Buy-in ($)"><Input type="number" value={entryFee} onChange={(e) => setEntryFee(e.target.value)} placeholder="0" className="bg-secondary border-border" data-testid="ct-fee" /></L>
            <L label="Number of Games"><Input type="number" value={numGames} onChange={(e) => setNumGames(e.target.value)} className="bg-secondary border-border" data-testid="ct-games" /></L>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <L label="Max Entries (0 = ∞)"><Input type="number" value={maxEntries} onChange={(e) => setMaxEntries(e.target.value)} placeholder="0" className="bg-secondary border-border" data-testid="ct-max" /></L>
            <L label="Payment Deadline">
              <input type="datetime-local" value={paymentDeadline} onChange={(e) => setPaymentDeadline(e.target.value)} className="bg-secondary border border-border rounded-sm px-3 h-10 w-full text-base" data-testid="ct-deadline" />
            </L>
          </div>
          <div className="flex items-center justify-between border border-border rounded-sm bg-secondary px-3 py-2.5">
            <div>
              <div className="font-semibold text-sm">Require admin approval</div>
              <div className="text-xs text-muted-foreground">Entries stay pending until you approve them</div>
            </div>
            <Switch checked={requiresApproval} onCheckedChange={setRequiresApproval} data-testid="ct-approval" />
          </div>
          <L label="First Game (Saturday) — auto-fortnightly">
            <input type="datetime-local" value={firstDate} onChange={(e) => setFirstDate(e.target.value)} className="bg-secondary border border-border rounded-sm px-3 h-10 w-full text-base" data-testid="ct-firstdate" />
          </L>
          <p className="text-xs text-muted-foreground">Leave date blank to set game dates later. Games auto-schedule a fortnight apart.</p>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={saving} data-testid="ct-submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest">{saving ? "Creating…" : "Create Tournament"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Users admin ---------------- */
function UsersAdmin({ isPrimary }) {
  const [users, setUsers] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [nickFor, setNickFor] = useState(null);

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const setRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (users === null) return <CircleNotch size={28} className="animate-spin text-primary" />;
  return (
    <div className="space-y-2">
      {users.map((u) => (
        <div key={u.id} className="border border-border bg-card rounded-sm p-3 flex items-center justify-between gap-3" data-testid={`admin-user-${u.id}`}>
          <div className="flex items-center gap-3 min-w-0">
            <Avatar name={u.nickname || u.name} path={u.avatar_path} size={38} />
            <div className="min-w-0">
              <div className="font-semibold truncate">{u.nickname || u.name}{u.nickname && <span className="text-xs text-muted-foreground font-normal ml-1">({u.name})</span>}</div>
              <div className="text-xs text-muted-foreground truncate">{u.email || u.phone}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isPrimary && u.role !== "primary_admin" ? (
              <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                <SelectTrigger className="w-28 h-9 bg-secondary border-border text-xs font-bold uppercase" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="player">Player</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-1">{u.role === "primary_admin" && <ShieldStar size={14} weight="fill" />}{u.role.replace("_", " ")}</span>
            )}
            <button onClick={() => setNickFor(u)} className="p-2 text-muted-foreground hover:text-white" title="Edit nickname" data-testid={`edit-nick-${u.id}`}><PencilSimple size={18} weight="bold" /></button>
            <button onClick={() => setResetFor(u)} className="p-2 text-muted-foreground hover:text-white" title="Reset password" data-testid={`reset-pw-${u.id}`}><Key size={18} weight="bold" /></button>
          </div>
        </div>
      ))}
      {resetFor && <ResetPassword user={resetFor} onClose={() => setResetFor(null)} />}
      {nickFor && <EditNickname user={nickFor} onClose={() => setNickFor(null)} onSaved={load} />}
    </div>
  );
}

function EditNickname({ user, onClose, onSaved }) {
  const [nick, setNick] = useState(user.nickname || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}/nickname`, { nickname: nick });
      toast.success(`Nickname updated for ${user.name}`);
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-sm">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">Edit Nickname</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">Screen name for <b className="text-white">{user.name}</b>. Leave blank to clear it.</p>
        <Input value={nick} onChange={(e) => setNick(e.target.value)} placeholder="Nickname" className="bg-secondary border-border" data-testid="admin-nickname-input" />
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-nickname" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest">{saving ? "Saving…" : "Save Nickname"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPassword({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/auth/admin/reset-password", { user_id: user.id, new_password: pw });
      toast.success(`Password reset for ${user.name}`);
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-sm">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">Reset Password</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">New password for <b className="text-white">{user.name}</b>.</p>
        <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="bg-secondary border-border" data-testid="new-password-input" />
        <DialogFooter>
          <Button onClick={save} disabled={saving || pw.length < 6} data-testid="confirm-reset" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest">{saving ? "Saving…" : "Set Password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Notifications admin ---------------- */
function NotificationsAdmin() {
  const [settings, setSettings] = useState(null);
  const [testMsg, setTestMsg] = useState("Test notification from Vegas 2027");

  const load = async () => setSettings((await api.get("/settings/notifications")).data);
  useEffect(() => { load(); }, []);

  const LEADS = { off: [], dayof: [0], "3days": [3, 1], daily: [7, 3, 1, 0] };
  const setFreq = async (reminder_frequency) => {
    await api.patch("/settings/notifications", { reminder_frequency, reminder_lead_days: LEADS[reminder_frequency] });
    toast.success("Reminder frequency updated");
    load();
  };
  const sendTest = async () => {
    try {
      const { data } = await api.post("/push/test", { title: "Vegas 2027", body: testMsg, url: "/" });
      toast.success(`Sent to ${data.sent} device(s)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (settings === null) return <CircleNotch size={28} className="animate-spin text-primary" />;
  return (
    <div className="max-w-lg space-y-4">
      <div className="border border-border bg-card rounded-sm p-6">
        <div className="flex items-center gap-2 mb-3"><BellRinging size={22} weight="fill" className="text-primary" /><h2 className="font-heading font-bold text-xl uppercase tracking-tight">Reminder Frequency</h2></div>
        <p className="text-sm text-muted-foreground mb-3">How often to remind players before an upcoming game.</p>
        <Select value={settings.reminder_frequency} onValueChange={setFreq}>
          <SelectTrigger className="bg-secondary border-border h-11" data-testid="freq-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="off">Off</SelectItem>
            <SelectItem value="dayof">Day of the game only</SelectItem>
            <SelectItem value="3days">3 days & day before</SelectItem>
            <SelectItem value="daily">Full countdown (7, 3, 1, 0 days)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="border border-border bg-card rounded-sm p-6">
        <div className="flex items-center gap-2 mb-3"><PaperPlaneTilt size={20} weight="fill" className="text-primary" /><h2 className="font-heading font-bold text-xl uppercase tracking-tight">Send Test Push</h2></div>
        <Input value={testMsg} onChange={(e) => setTestMsg(e.target.value)} className="bg-secondary border-border mb-3" data-testid="test-msg" />
        <Button onClick={sendTest} data-testid="send-test" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest">Send to all devices</Button>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
